import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FolderSimple,
  Receipt,
  Hammer,
  CreditCard,
} from "@phosphor-icons/react";
import { PortalShell } from "@/components/PortalShell";
import {
  ProjectCard,
  QuoteCard,
  JobCard,
  InvoiceCard,
} from "@/components/PortalCards";
import { portalApi, portalMoney, portalDate } from "@/lib/portalApi";
import { PortalEmpty } from "@/components/PortalEmpty";

const BADGE = {
  zinc: "border-slate-300 bg-slate-100 text-slate-500",
  blue: "border-blue-200 bg-blue-50 text-blue-700",
  emerald: "border-emerald-200 bg-emerald-50 text-emerald-600",
  amber: "border-amber-200 bg-amber-50 text-amber-600",
  red: "border-red-500/25 bg-red-50 text-red-600",
};

const STRIPE = {
  zinc: "bg-slate-400",
  blue: "bg-blue-500",
  emerald: "bg-emerald-500",
  amber: "bg-amber-500",
  red: "bg-red-500",
};

const tone = (stage = "") => {
  const s = stage.toLowerCase();
  if (["accepted", "complete", "installed", "paid"].some((k) => s.includes(k)))
    return "emerald";
  if (["declined", "void", "cancel"].some((k) => s.includes(k))) return "red";
  if (
    ["template", "fabrication", "install", "partly"].some((k) => s.includes(k))
  )
    return "amber";
  if (["sent", "quoted", "approved", "lead"].some((k) => s.includes(k)))
    return "blue";
  return "zinc";
};

const Head = ({ cols, grid }) => (
  <div
    className={`grid ${grid} gap-4 border-b border-slate-200 bg-slate-50 px-4 py-3 text-[11px] uppercase tracking-widest text-slate-500`}
  >
    {cols.map((c) => (
      <span key={c.key} className={c.right ? "text-right" : ""}>
        {c.label}
      </span>
    ))}
  </div>
);

const Row = ({ to, grid, stage, testId, children }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`relative grid ${grid} items-center gap-4 border-b border-slate-200 bg-white px-4 py-3 pl-5 transition-colors duration-150 last:border-0 hover:bg-slate-50`}
  >
    <span
      className={`absolute left-0 top-0 h-full w-1.5 ${STRIPE[tone(stage)]}`}
    />
    {children}
  </Link>
);

const Stage = ({ stage, testId }) => (
  <span
    data-testid={testId}
    className={`w-fit shrink-0 rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-widest ${
      BADGE[tone(stage)]
    }`}
  >
    {stage || "—"}
  </span>
);

const Name = ({ title, code, note }) => (
  <span className="min-w-0">
    <span className="block truncate text-sm font-bold text-slate-900">
      {title || "—"}
    </span>
    <span className="font-mono-data block text-[11px] text-teal-700">
      {code}
      {note && <span className="ml-2 text-emerald-600">{note}</span>}
    </span>
  </span>
);

const Two = ({ top, bottom }) => (
  <span className="min-w-0 text-xs text-slate-500">
    <span className="block truncate font-semibold text-slate-700">
      {top || "—"}
    </span>
    <span className="block truncate">{bottom || ""}</span>
  </span>
);

const QUOTE_GRID = "grid-cols-[1.7fr_1.4fr_1fr_0.6fr_0.9fr_0.9fr]";
const QuoteTable = ({ rows }) => (
  <div
    className="mt-8 hidden overflow-hidden rounded-2xl border border-slate-200 lg:block"
    data-testid="portal-quotes-table"
  >
    <Head
      grid={QUOTE_GRID}
      cols={[
        { key: "q", label: "Quote" },
        { key: "a", label: "Job address" },
        { key: "m", label: "Material" },
        { key: "s", label: "Sq ft" },
        { key: "st", label: "Stage" },
        { key: "t", label: "Total", right: true },
      ]}
    />
    {rows.map((q) => (
      <Row
        key={q.id}
        to={`/portal/quotes/${q.id}`}
        grid={QUOTE_GRID}
        stage={q.accepted_at ? "Accepted" : q.status || "Sent"}
        testId={`portal-quote-row-${q.id}`}
      >
        <Name
          title={q.project_name}
          code={q.code}
          note={q.accepted_at ? "accepted" : ""}
        />
        <Two top={q.job_address} bottom={portalDate(q.created_at)} />
        <span className="min-w-0 truncate text-xs text-slate-500">
          {q.slab_color || "—"}
        </span>
        <span className="font-mono-data text-sm text-slate-600">
          {q.sqft || 0}
        </span>
        <Stage
          stage={q.accepted_at ? "Accepted" : q.status || "Sent"}
          testId={`portal-quote-row-stage-${q.id}`}
        />
        <span className="font-mono-data text-right text-base font-bold text-slate-900">
          {q.total === null || q.total === undefined
            ? ""
            : portalMoney(q.total)}
        </span>
      </Row>
    ))}
  </div>
);

const JOB_GRID = "grid-cols-[1.7fr_1.4fr_0.9fr_0.9fr_0.9fr_0.9fr]";
const JobTable = ({ rows }) => (
  <div
    className="mt-8 hidden overflow-hidden rounded-2xl border border-slate-200 lg:block"
    data-testid="portal-jobs-table"
  >
    <Head
      grid={JOB_GRID}
      cols={[
        { key: "j", label: "Job" },
        { key: "a", label: "Address" },
        { key: "st", label: "Stage" },
        { key: "t", label: "Template" },
        { key: "f", label: "Fabrication" },
        { key: "i", label: "Install", right: true },
      ]}
    />
    {rows.map((j) => (
      <Row
        key={j.id}
        to={`/portal/jobs/${j.id}`}
        grid={JOB_GRID}
        stage={j.stage}
        testId={`portal-job-row-${j.id}`}
      >
        <Name title={j.project_name} code={j.code} />
        <Two top={j.address} bottom={j.summary} />
        <Stage stage={j.stage} testId={`portal-job-row-stage-${j.id}`} />
        <span className="text-xs text-slate-500">
          {portalDate(j.template_date) || "—"}
        </span>
        <span className="text-xs text-slate-500">
          {portalDate(j.fab_date) || "—"}
        </span>
        <span className="text-right text-xs font-semibold text-slate-700">
          {portalDate(j.install_date) || "—"}
        </span>
      </Row>
    ))}
  </div>
);

const PROJ_GRID = "grid-cols-[1.7fr_1.4fr_0.9fr_0.9fr_0.9fr_0.9fr]";
const ProjectTable = ({ rows }) => (
  <div
    className="mt-8 hidden overflow-hidden rounded-2xl border border-slate-200 lg:block"
    data-testid="portal-projects-table"
  >
    <Head
      grid={PROJ_GRID}
      cols={[
        { key: "p", label: "Project" },
        { key: "a", label: "Job address" },
        { key: "q", label: "Quotes" },
        { key: "st", label: "Stage" },
        { key: "i", label: "Install" },
        { key: "t", label: "Accepted", right: true },
      ]}
    />
    {rows.map((p) => (
      <Row
        key={p.id}
        to={`/portal/projects/${p.id}`}
        grid={PROJ_GRID}
        stage={p.stage}
        testId={`portal-project-row-${p.id}`}
      >
        <Name title={p.name} code={p.code} />
        <Two top={p.job_address} bottom={p.sqft ? `${p.sqft} ft²` : ""} />
        <span className="text-xs text-slate-500">
          {(p.quotes || []).length} quote
          {(p.quotes || []).length === 1 ? "" : "s"}
          <span className="block text-[10px] uppercase tracking-widest text-slate-400">
            {(p.jobs || []).length} job{(p.jobs || []).length === 1 ? "" : "s"}
          </span>
        </span>
        <Stage stage={p.stage} testId={`portal-project-row-stage-${p.id}`} />
        <span className="text-xs text-slate-500">
          {portalDate(p.target_install_date) || "—"}
        </span>
        <span className="font-mono-data text-right text-base font-bold text-slate-900">
          {p.accepted_total ? portalMoney(p.accepted_total) : "—"}
        </span>
      </Row>
    ))}
  </div>
);

const INVOICE_BADGE = {
  Sent: BADGE.blue,
  "Partly paid": BADGE.amber,
  Paid: BADGE.emerald,
  Draft: BADGE.zinc,
};

const InvoiceTable = ({ rows }) => {
  const navigate = useNavigate();
  return (
    <div
      className="mt-8 hidden overflow-x-auto rounded-2xl border border-slate-200 bg-white lg:block"
      data-testid="portal-invoices-table"
    >
      <table className="w-full min-w-[760px] text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-[11px] uppercase tracking-widest text-slate-500">
            <th className="px-4 py-3 text-left">Invoice</th>
            <th className="px-4 py-3 text-left">Project</th>
            <th className="px-4 py-3 text-left">For</th>
            <th className="px-4 py-3 text-left">Due</th>
            <th className="px-4 py-3 text-right">Amount</th>
            <th className="px-4 py-3 text-right">Balance</th>
            <th className="px-4 py-3 text-right">Status</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr
              key={r.id}
              data-testid={`portal-invoice-row-${r.id}`}
              onClick={() => navigate(`/portal/invoices/${r.id}`)}
              className="cursor-pointer border-b border-slate-200 transition-colors duration-150 last:border-0 hover:bg-slate-50"
            >
              <td className="px-4 py-3">
                <span className="font-mono-data text-teal-700">{r.code}</span>
                {r.quote_code && (
                  <span className="ml-2 text-[11px] text-slate-400">
                    {r.quote_code}
                  </span>
                )}
              </td>
              <td className="max-w-[14rem] truncate px-4 py-3 text-slate-900">
                {r.project_name || "—"}
              </td>
              <td className="max-w-[18rem] truncate px-4 py-3 text-slate-500">
                {r.title}
              </td>
              <td className="px-4 py-3 text-slate-500">
                {r.due_date ? portalDate(r.due_date) : "On install"}
              </td>
              <td className="font-mono-data px-4 py-3 text-right text-slate-700">
                {portalMoney(r.amount)}
              </td>
              <td className="font-mono-data px-4 py-3 text-right font-bold text-slate-900">
                {portalMoney(r.balance)}
              </td>
              <td className="px-4 py-3 text-right">
                <span
                  className={`rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest ${
                    INVOICE_BADGE[r.status] || INVOICE_BADGE.Draft
                  }`}
                >
                  {r.status}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const KINDS = {
  projects: {
    path: "/projects",
    title: "Projects",
    micro: "Everything we are doing for you",
    subtitle: "Each project holds its quotes, drawings and job dates.",
    icon: FolderSimple,
    emptyTitle: "No projects yet",
    empty:
      "Your project appears here the moment we send your first quote — with its drawing, slab layout and job dates.",
    ctaLabel: "Request a quote",
    ctaTo: "/portal/request",
    table: ProjectTable,
    render: (p) => <ProjectCard key={p.id} pr={p} />,
  },
  quotes: {
    path: "/quotes",
    title: "Quotes",
    micro: "Prices we have sent you",
    subtitle: "Open one to see the drawing, the slab layout and accept it.",
    icon: Receipt,
    emptyTitle: "No quotes yet",
    empty:
      "As soon as we price your job, your quote lands here so you can review the drawing and accept online.",
    ctaLabel: "Request a quote",
    ctaTo: "/portal/request",
    table: QuoteTable,
    render: (q) => <QuoteCard key={q.id} q={q} />,
  },
  jobs: {
    path: "/projects",
    title: "Jobs",
    micro: "Work in the shop",
    subtitle: "Follow template, fabrication and install as it happens.",
    icon: Hammer,
    emptyTitle: "No jobs in the shop yet",
    empty:
      "Once you accept a quote we template, fabricate and install — and you'll follow every stage right here.",
    ctaLabel: "See my quotes",
    ctaTo: "/portal/quotes",
    secondaryLabel: "Browse inventory",
    secondaryTo: "/portal/inventory",
    table: JobTable,
    render: (j) => <JobCard key={j.id} j={j} />,
  },
  invoices: {
    path: "/invoices",
    title: "Invoices",
    micro: "Deposits and balances",
    subtitle:
      "Open an invoice to pay by card or print a copy for your records.",
    icon: CreditCard,
    emptyTitle: "No invoices yet",
    empty:
      "An invoice shows up here the moment you accept a quote — pay by card or print a copy any time.",
    ctaLabel: "See my quotes",
    ctaTo: "/portal/quotes",
    table: InvoiceTable,
    render: (i) => <InvoiceCard key={i.id} i={i} />,
  },
};

export default function PortalList({ kind }) {
  const cfg = KINDS[kind];
  const [state, setState] = useState({ kind: null, rows: [] });

  useEffect(() => {
    let live = true;
    portalApi
      .get(cfg.path)
      .then(({ data }) => live && setState({ kind, rows: data || [] }))
      .catch(() => live && setState({ kind, rows: [] }));
    return () => {
      live = false;
    };
  }, [cfg.path, kind]);

  // rows always belong to the kind on screen, so switching pages never mixes shapes
  const loading = state.kind !== kind;
  const rows = loading ? [] : state.rows;

  const Icon = cfg.icon;
  const Table = cfg.table;
  return (
    <PortalShell
      back="/portal/home"
      micro={cfg.micro}
      title={cfg.title}
      subtitle={cfg.subtitle}
    >
      {loading ? (
        <div className="mt-8 grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="h-36 animate-pulse rounded-2xl border border-slate-200 bg-white"
            />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <PortalEmpty
          className="mt-8"
          testId={`portal-${kind}-empty`}
          icon={Icon}
          title={cfg.emptyTitle}
          text={cfg.empty}
          ctaLabel={cfg.ctaLabel}
          ctaTo={cfg.ctaTo}
          secondaryLabel={cfg.secondaryLabel}
          secondaryTo={cfg.secondaryTo}
        />
      ) : (
        <>
          <Table rows={rows} />
          <div
            className="mt-8 grid gap-3 md:grid-cols-2 lg:hidden"
            data-testid={`portal-${kind}-list`}
          >
            {rows.map(cfg.render)}
          </div>
        </>
      )}
    </PortalShell>
  );
}
